import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  DatePicker,
  DeleteButton,
  Form,
  FormField,
  FormRow,
  FormSection,
  Input,
  Text,
} from '../ui';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as zod from 'zod';
import { nullableDateToString, stringToNumber } from '../../lib/zod-util';
import type { SofForm__StatementOfFactsFragment } from '../../graphql';
import {
  useCreatePortActivityMutation,
  useCreateSofMutation,
  useDeletePortActivityMutation,
  useUpdatePortActivityMutation,
  useUpdateSofMutation,
} from '../../graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import type { Id } from '../../lib/util';
import { isDefined } from '../../lib/util';
import differenceWith from 'lodash/differenceWith';
import intersectionWith from 'lodash/intersectionWith';
import { toast } from 'react-toastify';

const SOFSchema = zod.object({
  id: zod.number().nullable(),
  portCallId: zod.number(),
  arrivalTime: zod.date().nullable(),
  pilotOnboard: zod.date().nullable(),
  berthed: zod.date().nullable(),
  noticeOfReadiness: zod.date().nullable(),
  loadingCommenced: zod.date().nullable(),
  loadingCompleted: zod.date().nullable(),
  sailingTime: zod.date().nullable(),
  portActivity: zod
    .array(
      zod.object({
        id: zod.number().nullable(),
        start: zod.date(),
        end: zod.date().nullable(),
        operation: zod.string().nullable(),
      }),
    )
    .optional(),
});

const SOFSaveSchema = SOFSchema.extend({
  arrivalTime: nullableDateToString,
  pilotOnboard: nullableDateToString,
  berthed: nullableDateToString,
  noticeOfReadiness: nullableDateToString,
  loadingCommenced: nullableDateToString,
  loadingCompleted: nullableDateToString,
  sailingTime: nullableDateToString,
  portActivity: SOFSchema.shape.portActivity.transform(
    (portActivity) =>
      portActivity?.map((activity) => ({
        ...activity,
        start: activity.start.toISOString(),
        end: activity.end?.toISOString() ?? null,
      })) ?? undefined,
  ),
});

type SOFFormFields = zod.infer<typeof SOFSchema>;

export interface SOFFormProps {
  onSave?: () => void;
  portCallId: number;
  portName: string;
  portTimeZone: string | null;
  sof?: SofForm__StatementOfFactsFragment;
  userId: number;
  readOnly?: boolean;
}

const SOFForm = ({
  onSave,
  portCallId,
  portName,
  portTimeZone,
  sof,
  userId,
  readOnly,
}: SOFFormProps): JSX.Element => {
  const [
    createSof,
    { data: createSofData, error: createSofError, loading: createSofLoading },
  ] = useCreateSofMutation();
  const [
    updateSof,
    { data: updateSofData, error: updateSofError, loading: updateSofLoading },
  ] = useUpdateSofMutation();
  const [
    createPortActivity,
    {
      data: createPortActivityData,
      error: createPortActivityError,
      loading: createPortActivityLoading,
    },
  ] = useCreatePortActivityMutation();
  const [
    updatePortActivity,
    {
      data: updatePortActivityData,
      error: updatePortActivityError,
      loading: updatePortActivityLoading,
    },
  ] = useUpdatePortActivityMutation();
  const [
    deletePortActivity,
    {
      data: deletePortActivityData,
      error: deletePortActivityError,
      loading: deletePortActivityLoading,
    },
  ] = useDeletePortActivityMutation();

  const saveLoading =
    createSofLoading ||
    updateSofLoading ||
    createPortActivityLoading ||
    updatePortActivityLoading ||
    deletePortActivityLoading;

  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<boolean>(false);

  useEffect(
    () =>
      setSaveSuccess(
        !!createSofData ||
          !!updateSofData ||
          !!createPortActivityData ||
          !!updatePortActivityData ||
          !!deletePortActivityData,
      ),
    [
      createSofData,
      updateSofData,
      createPortActivityData,
      updatePortActivityData,
      deletePortActivityData,
    ],
  );

  useEffect(
    () =>
      setSaveError(
        !!createSofError ||
          !!updateSofError ||
          !!createPortActivityError ||
          !!updatePortActivityError ||
          !!deletePortActivityError,
      ),
    [
      createSofError,
      updateSofError,
      createPortActivityError,
      updatePortActivityError,
      deletePortActivityError,
    ],
  );

  useEffect(() => {
    setSaveSuccess(false);
    setSaveError(false);
  }, [portCallId]);

  const defaultValues = useMemo<SOFFormFields>(
    () => ({
      id: sof?.id ?? null,
      portCallId,
      arrivalTime: sof?.arrivalTime ? new Date(sof.arrivalTime) : null,
      pilotOnboard: sof?.pilotOnboard ? new Date(sof.pilotOnboard) : null,
      berthed: sof?.berthed ? new Date(sof.berthed) : null,
      noticeOfReadiness: sof?.noticeOfReadiness
        ? new Date(sof.noticeOfReadiness)
        : null,
      loadingCommenced: sof?.loadingCommenced
        ? new Date(sof.loadingCommenced)
        : null,
      loadingCompleted: sof?.loadingCompleted
        ? new Date(sof.loadingCompleted)
        : null,
      sailingTime: sof?.sailingTime ? new Date(sof.sailingTime) : null,
      portActivity:
        sof?.portActivitiesByStatementOfFactsId.nodes
          .filter(isDefined)
          .map((activity) => ({
            id: activity.id,
            start: new Date(activity.start),
            end: activity.end ? new Date(activity.end) : null,
            operation: activity.operation ?? null,
          })) ?? [],
    }),
    [portCallId, sof],
  );

  const { control, handleSubmit, register, reset } = useForm<SOFFormFields>({
    resolver: zodResolver(SOFSchema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'portActivity',
  });

  useEffect(() => reset(defaultValues), [defaultValues, reset]);

  const onFormSubmit = async (data: SOFFormFields) => {
    try {
      const { id, portActivity, ...savedSof } = SOFSaveSchema.parse(data);

      const {
        arrivalTime,
        pilotOnboard,
        berthed,
        loadingCommenced,
        loadingCompleted,
        sailingTime,
      } = savedSof;
      if (arrivalTime && pilotOnboard && arrivalTime > pilotOnboard) {
        toast.error('Arrival needs to be before pilot onboard');
        return;
      }

      if (pilotOnboard && berthed && pilotOnboard > berthed) {
        toast.error('Pilot onboard needs to be before berthed');
        return;
      }

      if (berthed && loadingCommenced && berthed > loadingCommenced) {
        toast.error('Berthed needs to be before loading commenced');
        return;
      }

      if (
        loadingCommenced &&
        loadingCompleted &&
        loadingCommenced > loadingCompleted
      ) {
        toast.error('Loading commenced needs to be before loading completed');
        return;
      }

      if (loadingCompleted && sailingTime && loadingCompleted > sailingTime) {
        toast.error('Loading completed needs to be before sailing time');
        return;
      }

      if (typeof id === 'number') {
        await updateSof({
          variables: { input: { id, statementOfFactPatch: savedSof } },
        });

        if (portActivity) {
          const oldPortActivity =
            sof?.portActivitiesByStatementOfFactsId.nodes.filter(isDefined) ??
            [];

          const portActivityToDelete = differenceWith(
            oldPortActivity,
            portActivity,
            (a, b) => a.id === b.id,
          );
          const portActivityToUpdate = intersectionWith(
            portActivity,
            oldPortActivity,
            (a, b) =>
              a.id == b.id &&
              (a.start !== b.start ||
                a.end !== b.end ||
                a.operation !== b.operation),
          ).filter(
            (activity): activity is Id<typeof activity> => !!activity.id,
          );
          const portActivityToCreate = differenceWith(
            portActivity,
            oldPortActivity,
            (a, b) => a.id === b.id,
          );

          await Promise.all(
            portActivityToDelete.map((assortment) =>
              deletePortActivity({
                variables: { input: { id: assortment.id } },
              }),
            ),
          );
          await Promise.all(
            portActivityToUpdate.map(({ id, ...patch }) =>
              updatePortActivity({
                variables: {
                  input: { id, portActivityPatch: patch },
                },
              }),
            ),
          );
          await Promise.all(
            portActivityToCreate.map(({ id: _activityId, ...activity }) =>
              createPortActivity({
                variables: {
                  input: {
                    portActivity: {
                      userId,
                      statementOfFactsId: id,
                      ...activity,
                    },
                  },
                },
              }),
            ),
          );
        }
      } else {
        const { data } = await createSof({
          variables: { input: { statementOfFact: { userId, ...savedSof } } },
        });

        const createdSofId = data?.createStatementOfFact?.statementOfFact?.id;

        if (portActivity && typeof createdSofId === 'number') {
          await Promise.all(
            portActivity.map(({ id: _activityId, ...activity }) =>
              createPortActivity({
                variables: {
                  input: {
                    portActivity: {
                      userId,
                      statementOfFactsId: createdSofId,
                      ...activity,
                    },
                  },
                },
              }),
            ),
          );
        }
      }

      onSave?.();
    } catch (error) {
      console.error('Error saving SOF', error);
    }
  };

  return (
    <Form border="none">
      <input
        type="hidden"
        {...register('id', { setValueAs: stringToNumber })}
      />
      <input
        type="hidden"
        {...register('portCallId', { setValueAs: stringToNumber })}
      />
      <FormSection title="Statement of facts">
        <FormRow>
          <FormField width={200} label="Port">
            <Input readOnly disabled value={portName} />
          </FormField>
          <FormField width={200} label="Arrival">
            <Controller
              control={control}
              name="arrivalTime"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField width={200} label="Pilot onboard">
            <Controller
              control={control}
              name="pilotOnboard"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField width={200} label="Berthed">
            <Controller
              control={control}
              name="berthed"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
        </FormRow>
        <FormRow mb={3} alignItems="flex-end">
          <FormField width={200} label="Notice of readiness">
            <Controller
              control={control}
              name="noticeOfReadiness"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField width={200} label="Loading/discharge commenced">
            <Controller
              control={control}
              name="loadingCommenced"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField width={200} label="Loading/discharge completed">
            <Controller
              control={control}
              name="loadingCompleted"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
          <FormField width={200} label="Sailing time">
            <Controller
              control={control}
              name="sailingTime"
              render={({ field: { value, onChange } }) => (
                <DatePicker
                  onChange={onChange}
                  selected={value}
                  showTime
                  showTimeInput
                  hideTimePickerTypes
                  defaultTimePickerType="CUSTOM"
                  timeZone={portTimeZone ?? undefined}
                  disabled={readOnly}
                />
              )}
            />
          </FormField>
        </FormRow>
        <FormRow>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            style={{
              minHeight: '40px',
              minWidth: '824px',
              marginRight: '5px',
              marginBottom: '10px',
              padding: '10px',
              flex: '0 0 auto',
              borderStyle: 'dashed',
              borderWidth: !readOnly ? '1px' : '0',
              borderColor: 'rgba(0, 0, 0, 0.41)',
            }}
          >
            {fields.map((field, index) => (
              <FormRow key={field.id} mb={0}>
                <input
                  type="hidden"
                  {...register(`portActivity.${index}.id`, {
                    setValueAs: stringToNumber,
                  })}
                  defaultValue={field.id}
                />
                <FormField
                  label={index === 0 ? 'Stop commenced' : undefined}
                  width={200}
                  mb={0}
                  required
                >
                  <Controller
                    control={control}
                    name={`portActivity.${index}.start`}
                    render={({ field: { value, onChange } }) => (
                      <DatePicker
                        onChange={onChange}
                        selected={value}
                        showTime
                        showTimeInput
                        hideTimePickerTypes
                        defaultTimePickerType="CUSTOM"
                        timeZone={portTimeZone ?? undefined}
                        disabled={readOnly}
                      />
                    )}
                    defaultValue={field.start || null}
                  />
                </FormField>
                <FormField
                  label={index === 0 ? 'Stop ended' : undefined}
                  width={200}
                  mb={0}
                >
                  <Controller
                    control={control}
                    name={`portActivity.${index}.end`}
                    render={({ field: { value, onChange } }) => (
                      <DatePicker
                        onChange={onChange}
                        selected={value}
                        showTime
                        showTimeInput
                        hideTimePickerTypes
                        defaultTimePickerType="CUSTOM"
                        timeZone={portTimeZone ?? undefined}
                        disabled={readOnly}
                      />
                    )}
                    defaultValue={field.end || null}
                  />
                </FormField>
                <FormField
                  label={index === 0 ? 'Operation' : undefined}
                  width={200}
                  mb={0}
                >
                  <Input
                    {...register(`portActivity.${index}.operation`)}
                    defaultValue={field.operation ?? undefined}
                  />
                </FormField>
                <FormField width={98} pt={2}>
                  <DeleteButton onPress={() => remove(index)} />
                </FormField>
              </FormRow>
            ))}
            {!readOnly && (
              <Button
                variant="secondary"
                my={4}
                onPress={() =>
                  append({
                    id: null,
                    start: new Date(),
                    end: null,
                    operation: null,
                  })
                }
              >
                + Add stop in loading/discharge
              </Button>
            )}
          </Box>
        </FormRow>
        {!readOnly && (
          <FormRow alignItems="center">
            <Button
              disabled={saveLoading}
              variant="secondary"
              alignSelf="flex-start"
              onPress={handleSubmit(onFormSubmit)}
              mr={3}
            >
              Save Statement of Facts
            </Button>
            {saveSuccess && (
              <Text fontWeight="bold" color="#5fad09">
                ✓ Saved successfully
              </Text>
            )}
            {saveError && (
              <Text fontWeight="bold" color="#FC4A15">
                ✗ Error saving
              </Text>
            )}
          </FormRow>
        )}
      </FormSection>
    </Form>
  );
};

export default SOFForm;
